import { apiFetch } from "@/lib/api";

export const BOX_LABEL_GENERATE_BATCH_KEY = ["manufacturing", "box-label-generate", "batch"] as const;

export interface BoxLabelGenerateBatchHeader {
  batch_id: number;
  date_created: string;
  mfr_ship_by_date: string | null;
  ship_to_recipient: string | null;
  manufacturer_sales_order_number: string | null;
  location_id: number;
  location_label: string;
}

export interface BoxLabelGenerateLineRow {
  batch_item_id: number;
  build_order_id: number | null;
  build_order_number: string | null;
  sku: string;
  batch_item_quantity: number;
  quantity_assembled: number;
  device_label_scanned_count: number;
  hub_box_supported: boolean;
  ready_to_print: boolean;
  block_reason: string | null;
}

export interface BoxLabelGenerateSerialRow {
  serial_fci_id: number;
  device_label_serial: string;
  finished_sku: string;
  box_serial: string | null;
  station_name: string | null;
}

export interface BoxLabelGenerateBatchResponse {
  batch: BoxLabelGenerateBatchHeader;
  lines: BoxLabelGenerateLineRow[];
}

export interface BoxLabelGenerateLineResponse {
  batch: BoxLabelGenerateBatchHeader;
  line: BoxLabelGenerateLineRow;
  serials: BoxLabelGenerateSerialRow[];
}

export interface BoxLabelGeneratePreviewResponse {
  device_label_serial: string;
  finished_sku: string;
  zpl: string;
  png_base64: string;
  preview_dpi: number;
}

function boxLabelGenerateBatchPath(batchId: number): string {
  return `/api/manufacturing/box-label-generate/batches/${String(batchId)}`;
}

function boxLabelGenerateLinePath(batchId: number, batchItemId: number): string {
  return `${boxLabelGenerateBatchPath(batchId)}/lines/${String(batchItemId)}`;
}

export function fetchBoxLabelGenerateBatch(batchId: number): Promise<BoxLabelGenerateBatchResponse> {
  return apiFetch<BoxLabelGenerateBatchResponse>(boxLabelGenerateBatchPath(batchId));
}

export function fetchBoxLabelGenerateLine(
  batchId: number,
  batchItemId: number,
): Promise<BoxLabelGenerateLineResponse> {
  return apiFetch<BoxLabelGenerateLineResponse>(boxLabelGenerateLinePath(batchId, batchItemId));
}

export function fetchBoxLabelGeneratePreview(input: {
  batchId: number;
  batchItemId: number;
  deviceLabelSerial?: string;
  printer?: string;
}): Promise<BoxLabelGeneratePreviewResponse> {
  const params = new URLSearchParams();
  if (input.deviceLabelSerial != null && input.deviceLabelSerial.trim() !== "") {
    params.set("deviceLabelSerial", input.deviceLabelSerial.trim());
  }
  if (input.printer != null && input.printer.trim() !== "") {
    params.set("printer", input.printer.trim());
  }
  const query = params.toString();
  const path = `${boxLabelGenerateLinePath(input.batchId, input.batchItemId)}/preview${query === "" ? "" : `?${query}`}`;
  return apiFetch<BoxLabelGeneratePreviewResponse>(path);
}

export function testPrintBoxLabelGenerateLine(input: {
  batchId: number;
  batchItemId: number;
  printer: string;
  deviceLabelSerial?: string;
}): Promise<{ printed: boolean; device_label_serial: string }> {
  return apiFetch<{ printed: boolean; device_label_serial: string }>(
    `${boxLabelGenerateLinePath(input.batchId, input.batchItemId)}/test-print`,
    {
      method: "POST",
      body: {
        printer: input.printer,
        deviceLabelSerial: input.deviceLabelSerial,
      },
    },
  );
}

export function printAllBoxLabelGenerateLine(input: {
  batchId: number;
  batchItemId: number;
  printer: string;
}): Promise<{ printed: boolean; printed_count: number; total_count: number }> {
  return apiFetch<{ printed: boolean; printed_count: number; total_count: number }>(
    `${boxLabelGenerateLinePath(input.batchId, input.batchItemId)}/print-all`,
    {
      method: "POST",
      body: { printer: input.printer },
    },
  );
}
